import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Form as AntdForm, Modal } from 'antd'
import { Button, Form, Input, FormItem } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Heading } from '@lmc/ui-kit/src/general'
import { Carer } from '@lmc/ts-common'
import { getFullName } from '@lmc/ts-common/lib/utils/functions'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import useUser from 'hooks/useUser/useUser'
import { Text } from '@lmc/ui-kit/src/general'
import { showToast } from 'common/utils'

export type DeleteMemberProps = {
  open: boolean
  onClose: () => void
  carer?: Carer
}

const DeleteProfileModal = ({ open, onClose, carer }: DeleteMemberProps) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)

  const [form] = AntdForm.useForm()

  const fullName = useMemo(
    () => (carer ? getFullName(carer.name.first, carer.name.last) : undefined),
    [carer]
  )

  const { deleteUser, isLoadingDelete } = useUser({
    onDeleteSuccess: () => onClose(),
    onErrorCustom: (err) => {
      const message = (err as { message: string }).message as string
      showToast({
        title: message,
        type: 'error',
      })
      form.setFields([
        {
          name: 'fullName',
          errors: [message],
        },
      ])
    },
    id: carer?.id,
  })

  const onSubmit = async () => {
    deleteUser()
  }

  const validateMatch = (rule: any, value: string) => {
    const expectedValue = fullName
    if (value === expectedValue) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(t(generalConstants.TYPE_NAME)))
  }

  return (
    <Modal
      destroyOnClose
      width={580}
      title={
        <ModalHeader>
          <Title level={1}>{t(generalConstants.DELETE_PROFILE)}</Title>
        </ModalHeader>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t(generalConstants.CANCEL)}</Button>
          <Button
            onClick={form.submit}
            isLoading={isLoadingDelete}
            variant="primary"
          >
            {t(generalConstants.CONFIRM, {})}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
    >
      <Text>
        Are you sure you want to delete <strong>{fullName}</strong> ? If you go
        ahead, it can’t be undone, Once the record is gone, it’s gone for good!
      </Text>
      <WrapperMiddle>
        <Text>{t(generalConstants.MAKE_SURE_TO_DELETE)}</Text>
      </WrapperMiddle>
      <WrapperType>
        <Text>
          <strong>{t(generalConstants.TYPE_NAME)}</strong>
        </Text>
      </WrapperType>
      <StyledForm form={form} layout="vertical" onFinish={onSubmit}>
        <FormItem
          emptyLabel
          name="fullName"
          label={t(generalConstants.EMAIL)}
          style={{ width: '100%' }}
          rules={[
            { required: true, message: 'This field is required' },
            { validator: validateMatch },
          ]}
        >
          <Input placeholder={fullName} />
        </FormItem>
      </StyledForm>
      <ModalDivider />
    </Modal>
  )
}

export default DeleteProfileModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const WrapperType = styled.div`
  margin-bottom: -20px;
  margin-top: 20px;
`

const WrapperMiddle = styled.div`
  margin-top: 20px;
`

const StyledForm = styled(Form)`
  gap: 24px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  text-transform: none;
`
